import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, OnChanges, SimpleChanges, OnInit, HostListener } from '@angular/core';
import { SpreadingState } from "@components/main/spreading/state/manager";
import { Row } from "@components/main/spreading/models/models";
import { DocumentItemCategorizePayload } from '../../categorization-view.types';
import { debounce } from '../../utils/debounce';
import { editableSourceLabel, isRowLinkedToTemplate } from '@components/main/spreading/spreading-display-helpers';

const SEARCH_DEBOUNCE_MS = 250;

export type DocumentItemMode = 'component' | 'validation';
const COMPONENT_TOOLTIP_TEXT = 'Categorizes a document value to template fields for calculations and ratios';
const VALIDATION_TOOLTIP_TEXT = 'Checks that a document value aligns with the calculated value in the template';

@Component({
  selector: 'app-categorization-document-item-view',
  templateUrl: './document-item-view.component.html',
  styleUrls: ['./document-item-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DocumentItemViewComponent implements OnInit, OnChanges {
  @Input() state: SpreadingState;
  @Input() row: Row;
  @Input() rollupOnly = false;
  @Input() viewInNativeLanguage = true;

  @Output() categorizeSelected = new EventEmitter<DocumentItemCategorizePayload>();
  documentItemMode: DocumentItemMode = 'component';
  selectedSourceRowUuids = new Set<string>();
  showCategorizedItems = false;
  searchQuery = '';
  debouncedSearchQuery = '';

  private readonly searchDebouncer = debounce<string>(SEARCH_DEBOUNCE_MS, (term: string) => {
    this.debouncedSearchQuery = term;
    this.cdr.detectChanges();
  });

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.applyRollupOnlyMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rollupOnly']) {
      this.applyRollupOnlyMode();
    }
  }

  private applyRollupOnlyMode(): void {
    if (this.rollupOnly) {
      this.documentItemMode = 'validation';
      this.cdr.detectChanges();
    }
  }

  get primaryActionLabel(): string {
    const n = this.selectedSourceRowUuids.size;
    const base =
      this.documentItemMode === 'validation' ? 'Validate Selected' : 'Categorize Selected';
    return `${base} (${n})`;
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    const trimmed = (value || '').trim().toLowerCase();
    if (trimmed === '') {
      this.searchDebouncer.cancel();
      this.debouncedSearchQuery = '';
      this.cdr.detectChanges();
      return;
    }
    this.searchDebouncer.run(trimmed);
  }

  toggleShowCategorizedItems(): void {
    this.showCategorizedItems = !this.showCategorizedItems;
    this.cdr.detectChanges();
  }

  get displayedSourceRows(): Row[] {
    if (!this.state?.sourceRows) {
      return [];
    }
    if (!this.showCategorizedItems) {
      return this.state.sourceRows.filter((r) => !r.header && !isRowLinkedToTemplate(this.state, r));
    }
    return this.state.sourceRows;
  }

  get filteredDisplayedSourceRows(): Row[] {
    const term = this.debouncedSearchQuery;
    if (!term) {
      return this.displayedSourceRows;
    }
    return this.displayedSourceRows.filter((row) =>
      this.displayLabel(row).toLowerCase().includes(term)
    );
  }

  displayLabel(sourceRow: Row): string {
    return editableSourceLabel(sourceRow, this.viewInNativeLanguage) ?? '';
  }

  isCategorized(sourceRow: Row): boolean {
    return isRowLinkedToTemplate(this.state, sourceRow);
  }

  isSelected(sourceRow: Row): boolean {
    return sourceRow.uuid ? this.selectedSourceRowUuids.has(sourceRow.uuid) : false;
  }

  onSourceItemClick(sourceRow: Row): void {
    if (this.isCategorized(sourceRow) && !this.isSelected(sourceRow)) {
      return;
    }
    this.toggleSelection(sourceRow);
  }

  toggleSelection(sourceRow: Row): void {
    if (!sourceRow.uuid) return;
    const validation = this.documentItemMode === 'validation';
    if (this.selectedSourceRowUuids.has(sourceRow.uuid)) {
      const next = new Set(this.selectedSourceRowUuids);
      next.delete(sourceRow.uuid);
      this.selectedSourceRowUuids = next;
    } else if (validation) {
      this.selectedSourceRowUuids = new Set([sourceRow.uuid]);
    } else {
      const next = new Set(this.selectedSourceRowUuids);
      next.add(sourceRow.uuid);
      this.selectedSourceRowUuids = next;
    }
    this.cdr.detectChanges();
  }

  selectForReassign(sourceRow: Row): void {
    if (!sourceRow.uuid) return;
    if (this.documentItemMode === 'validation') {
      this.selectedSourceRowUuids = new Set([sourceRow.uuid]);
    } else {
      const next = new Set(this.selectedSourceRowUuids);
      next.add(sourceRow.uuid);
      this.selectedSourceRowUuids = next;
    }
    this.cdr.detectChanges();
  }

  get hasSelection(): boolean {
    return this.selectedSourceRowUuids.size > 0;
  }

  getSelectedSourceRows(): Row[] {
    if (!this.state?.sourceRows) {
      return [];
    }
    return this.state.sourceRows.filter(
      (r) => !r.header && r.uuid && this.selectedSourceRowUuids.has(r.uuid)
    );
  }

  onCategorizeSelected(): void {
    if (!this.hasSelection) {
      return;
    }
    const selected = this.getSelectedSourceRows();
    if (selected.length > 0) {
      const isValidation = this.documentItemMode === 'validation';
      this.categorizeSelected.emit({ rows: selected, isValidation });
    }
  }

  onListKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    const list = event.currentTarget as HTMLElement;
    const focusables = Array.from(
      list.querySelectorAll<HTMLElement>('input[type="checkbox"]')
    ).filter(el => el.offsetParent !== null);
    if (!focusables.length) {
      return;
    }
    event.preventDefault();
    const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0
      ? 0
      : Math.min(Math.max(currentIndex + delta, 0), focusables.length - 1);
    focusables[nextIndex]?.focus();
  }

  // Document-level so Enter submits even when focus has moved off the list (e.g.
  // toggling a checkbox re-renders the row and focus falls back to the body).
  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: KeyboardEvent): void {
    // Buttons and the toggle handle Enter themselves; let those act instead of submitting.
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, [role="button"]')) {
      return;
    }
    if (this.hasSelection) {
      event.preventDefault();
      this.onCategorizeSelected();
    }
  }

  protected readonly COMPONENT_TOOLTIP_TEXT = COMPONENT_TOOLTIP_TEXT;
  protected readonly VALIDATION_TOOLTIP_TEXT = VALIDATION_TOOLTIP_TEXT;
}
